// ============================================================================
// BUILD PATIENT APPROVED (NO PHI)
//
// Pure function. Generates a plain-language patient-facing confirmation that
// their referral has been approved (ROUTINE) or flagged urgent (URGENT).
//
// ZERO PHI: no patient name / DOB / MRN / contact. Only specialty name and
// reference token — information the patient already knows about their own care.
// ============================================================================

/**
 * Build a patient-facing approved-referral notification message.
 *
 * @param {object} params
 * @param {string|null} params.receiptToken  - RG-XXXXXXX stable token
 * @param {string}      params.referralId    - Firestore referralId (fallback)
 * @param {string}      params.specialtyName - Human-readable specialty name
 * @param {string|null} params.submittedAt   - ISO timestamp of submission
 * @param {'routine'|'urgent'} params.outcome
 * @returns {string} Plain-text message ready for clipboard
 */
export function buildPatientApprovedNoPhi({
  receiptToken = null,
  referralId = '',
  specialtyName = 'Specialist',
  submittedAt = null,
  outcome = 'routine',
}) {
  const ref = receiptToken || (referralId ? referralId.slice(0, 12) : 'N/A');

  const dateStr = submittedAt
    ? new Date(submittedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'recently';

  if (outcome === 'urgent') {
    return [
      `RE: YOUR REFERRAL TO ${specialtyName.toUpperCase()} — URGENT`,
      `Date: ${dateStr}`,
      ``,
      `Your referral to ${specialtyName} has been submitted as urgent.`,
      ``,
      `Our team will contact you very shortly to arrange your appointment.`,
      `If you do not hear from us within 24 hours, please call our office.`,
      ``,
      `Reference: ${ref}`,
      `This message contains no personal health information.`,
    ].join('\n');
  }

  return [
    `RE: YOUR REFERRAL TO ${specialtyName.toUpperCase()} — APPROVED`,
    `Date: ${dateStr}`,
    ``,
    `Your referral to ${specialtyName} has been reviewed and approved.`,
    ``,
    `Our scheduling team will be in contact to arrange your appointment.`,
    `If you have not heard from us within 5 business days, please contact our office.`,
    ``,
    `Reference: ${ref}`,
    `This message contains no personal health information.`,
  ].join('\n');
}
